import { useLocation, useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback } from "react";

import { flag, oneOf, optionalString } from "@/lib/search";

/**
 * The upload dialog is a modal route (`docs/design/ia.md` §1): it opens over whichever screen is
 * underneath, the URL shows `/upload` (route masking), and a direct visit to `/upload` renders it
 * over the library. Screens that can open it accept `upload` and `machine` search params and
 * spread `uploadSearch` into their `validateSearch`.
 */

export interface UploadSearch {
  instrument?: string;
  procedure?: string;
  /** Set while the dialog is open (masked as `/upload`). */
  upload?: true;
  /** Preselects "Add to an existing machine" with this machine key. */
  machine?: string;
  /** Preselects "Always create a new machine" (the library's "+ Add a machine"). */
  mode?: UploadModeParam;
}

export const UPLOAD_MODE_PARAMS = ["new"] as const;
export type UploadModeParam = (typeof UPLOAD_MODE_PARAMS)[number];

export function uploadSearch(raw: Record<string, unknown>): UploadSearch {
  // Keys are always present: the router merges a route's validated search over the raw one, so
  // an omitted key would let an invalid value through. Undefined values are dropped from the URL.
  return {
    instrument: optionalString(raw.instrument),
    procedure: optionalString(raw.procedure),
    upload: flag(raw.upload),
    machine: optionalString(raw.machine),
    mode: oneOf(UPLOAD_MODE_PARAMS, raw.mode),
  };
}

declare module "@tanstack/react-router" {
  interface HistoryState {
    /** Set by `useOpenUpload` so closing can go back instead of pushing another entry. */
    uploadOpener?: boolean;
  }
}

export const UPLOAD_PATH = "/upload";

/** Whether the dialog should be open for the current location. */
export function useUploadOpen(): boolean {
  return useRouterState({
    select: (state) =>
      state.location.pathname === UPLOAD_PATH ||
      (state.location.search as UploadSearch).upload === true,
  });
}

/** The preselected machine key (from `?machine=`), if any. */
export function useUploadMachine(): string | undefined {
  return useRouterState({ select: (state) => (state.location.search as UploadSearch).machine });
}

/** The preselected mode (from `?mode=new`), if any. */
export function useUploadMode(): UploadModeParam | undefined {
  return useRouterState({ select: (state) => (state.location.search as UploadSearch).mode });
}

export interface OpenUploadOptions {
  instrumentId?: string;
  procedureId?: string;
  /** `"new"` preselects "Always create a new machine". Ignored when `machine` is given. */
  mode?: UploadModeParam;
}

/** Open the dialog over the current screen; the URL shows `/upload[?machine=|?mode=new]`. */
export function useOpenUpload(): (machine?: string, options?: OpenUploadOptions) => void {
  const navigate = useNavigate();
  return useCallback(
    (machine?: string, options?: OpenUploadOptions) => {
      const mode = machine ? undefined : options?.mode;
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          upload: true,
          machine,
          mode,
          instrument: options?.instrumentId,
          procedure: options?.procedureId,
        }),
        mask: {
          to: UPLOAD_PATH,
          search: {
            machine,
            mode,
            instrument: options?.instrumentId,
            procedure: options?.procedureId,
          },
        },
        state: { uploadOpener: true },
      } as never);
    },
    [navigate],
  );
}

/** Close the dialog: go back when we opened it, else strip the params (or leave `/upload`). */
export function useCloseUpload(): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  return useCallback(() => {
    if (location.state.uploadOpener && window.history.length > 1) {
      window.history.back();
      return;
    }
    if (location.pathname === UPLOAD_PATH) {
      void navigate({ to: "/", replace: true });
      return;
    }
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const {
          upload: _upload,
          machine: _machine,
          mode: _mode,
          instrument: _instrument,
          procedure: _procedure,
          ...rest
        } = prev;
        return rest;
      },
      replace: true,
    } as never);
  }, [navigate, location.pathname, location.state.uploadOpener]);
}
